import * as React from 'react';
import cx from 'classnames';
import px from 'prop-types';
import { useViewport, useTranslation, useOrderRequestState } from 'Common/hooks';
import { Url, GTM as gtmClass } from 'Common/utils';
import BuyInBulkAccordion from './BuyInBulkAccordion';
import BuyBulkProductCard from './BuyBulkProductCard'
import BuyBulkSummary from './BuyBulkSummary';
import * as PRODUCT from '~config/product';
import ProductPrice from '../../ProductPrice';
import ProductBadge from '../../ProductBadge';
import { Product } from 'Common/types';
import BulkAddToCart from './BulkAddToCart';
import { Loader, NumberInput } from 'Common/components/ui';
import ScrollIndicator from 'Common/components/ui/ScrollIndicator';

// Helper functions
function isQuantityValid(value = 0) {
    const zeroOrPositiveIntegerRegex = /^(0|[1-9][0-9]*)$/;

    return zeroOrPositiveIntegerRegex.test(value.toString());
}

const isValidItem = (row) => row && row.quantity && parseInt(row.quantity) > 0 && !row.disabled;

const getImageUrl = (imageUrl, height, width) => 
    Url.addToRelativeUrl(
        imageUrl,
        `format=png&height=${height}&width=${width}${
            PRODUCT.TRANSFORM_PRODUCT_IMAGE_BACKGROUND ? '&transBg=true' : ''
        }&fuzziness=40`
    );

// Helper function to get facet value for a product
const getFacetValueForProduct = (productData, facetAttribute) => {
    // Primary method - check productInfo.Attributes array
    if (productData.productInfo?.Attributes) {
        const matchingAttribute = productData.productInfo.Attributes.find(
            attr => attr.AttributeName === facetAttribute
        );
        
        if (matchingAttribute?.Values?.length > 0) {
            return matchingAttribute.Values[0];
        }
    }
    
    // Fallback - check if the product has facet information (legacy)
    if (productData.productInfo?.facets) {
        const facetValue = productData.productInfo.facets[facetAttribute];
        
        if (facetValue) {
            return facetValue;
        }
    }
    
    return '-'; // Default if no value found
};

export default function ProductBuyInBulk({
    bulkAddItemsToCart,
    className,
    buyInBulkTableData,
    gtmListValue,
    id,
    orderLastUpdate,
    forceMobile = false,
    Price = ProductPrice,
    labelledBy = id?.replace(/content/g, 'tab'),
    statusTooltip,
    totalQty,
    productFacets = [],
    productInfo
}) {
    const viewport = useViewport();
    const [isLoading, setIsLoading] = React.useState(false);
    const [rows, setRows] = React.useState({});

    const quantityLabel = useTranslation('Commerce.Product.Tabs.BuyInBulk.Quantity.Label');
    const priceLabel = useTranslation('Commerce.Product.Tabs.Price.Label');
    const typeIdLabel = useTranslation('Commerce.Product.Tabs.TypeId.Label');
    const skuLabel = useTranslation('Commerce.Product.Tabs.Sku.Label');
    
    const cartIsUpdating = useOrderRequestState();

    const [addToCartTip, setAddToCartTip] = React.useState({
        timestamp: 0,
        message: null,
        params: null,
        level: 'info',
        placement: 'top',
        hidden: true
    });

    const updateRow = React.useCallback(
        (rowId, state) => {
            setAddToCartTip({
                timestamp:0,
                message:null,
                hidden: true,
            })
            setRows(prevRows => ({ ...prevRows, [rowId]: state }));
        }, []
    );

    const clearAllInputs = React.useCallback(() => {
        setRows({});
    }, []);

    // Create accordion headers including facets
    const accordionHeaders = React.useMemo(() => {
        const baseHeaders = [typeIdLabel, skuLabel];
        
        // Add facet headers
        const facetHeaders = productFacets.map(facet => 
            facet.AttributeDisplayName || facet.Attribute
        );
        
        return [...baseHeaders, ...facetHeaders, priceLabel, quantityLabel];
    }, [priceLabel, quantityLabel, skuLabel, typeIdLabel, productFacets]);

    // includes both statistics and summary data
    const selectedItems = React.useMemo(() => {
        const items = Object.entries(rows)
            // eslint-disable-next-line no-unused-vars
            .filter(([_, rowState]) => isValidItem(rowState))
            .map(([rowId, rowState]) => {
                const productData = buyInBulkTableData.find(data => data.id === rowId);
                const quantity = parseInt(rowState.quantity);

                return {
                    id: rowId,
                    quantity,
                    productData,
                    rowState
                };
            })
            .filter(item => item.productData); 

        const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

        return {
            items,
            stats: {
                hasValidItems: items.length > 0,
                itemCount: items.length,
                totalQuantity,
                validItems: items.map(item => item.rowState) 
            }
        };
    }, [rows, buyInBulkTableData]);

    const getNumberInputProps = React.useCallback((data) => ({
        allowZero: true,
        isLoading: isLoading,
        min: 0,
        noDecimal: true,
        onChange: (inputValue) =>
            updateRow(data.id, {
                quantity: inputValue,
                disabled: !isQuantityValid(inputValue),
            }),
        value: rows[data.id]?.quantity ?? 0,
        step: 1,
        useStepBtns: true,
    }), [isLoading, rows, updateRow])

    const onBulkAddToCart = React.useCallback(async (itemsData) => {
        const itemsToAdd = itemsData.items
            .filter(item => item.productData?.hasStock);

        const codeToQtyMap = {};

        itemsToAdd.forEach(item => {
            codeToQtyMap[item.id] = item.quantity;
        });

        setIsLoading(true);

        const impressionPayload = gtmClass.mapEntityToImpressions([productInfo] , gtmListValue);
        
        if (impressionPayload) gtmClass.updateDataLayer(impressionPayload);

        const gtmPayload = gtmClass.bulkMapEntityToAddCart(itemsToAdd, gtmListValue, totalQty);
        
        try {
            // Single call to add all items
            await bulkAddItemsToCart(codeToQtyMap, {
                statusCallback: statusTooltip ? (successMsg, validationIssues = []) => {
                    
                    const failedCodes = validationIssues
                        .filter(issue => issue.RefType === 'LineItem')
                        .map(issue => issue.Ref);
                        
                    const successfulCodes = Object.keys(codeToQtyMap)
                        .filter(code => !failedCodes.includes(code));
                    
                    const totalQuantity = Object.values(codeToQtyMap).reduce((sum, qty) => sum + qty, 0);
                
                    if (successMsg) {
                        // All items successful
                        setAddToCartTip({
                            message: successMsg,
                            timestamp: Date.now(),
                            placement: 'top',
                            level: 'success',
                            params: { qty: totalQuantity },
                        });
                        clearAllInputs();
                    } else if (validationIssues?.length > 0) {
                        // Some items had validation issues
                        
                        if (successfulCodes.length > 0) {
                            // Mixed results - clear only successful items
                            const newRows = { ...rows };

                            successfulCodes.forEach(code => {
                                delete newRows[code];
                            });
                            setRows(newRows);
                            
                            const errorMessage = validationIssues
                                .map(issue => `${issue.Ref}: ${issue.Issue}`)
                                .join('; ');
                            
                            setAddToCartTip({
                                message: `${successfulCodes.length} item${successfulCodes.length > 1 ? 's' : ''} added successfully. Failed: ${errorMessage}`,
                                timestamp: Date.now(),
                                placement: 'top',
                                level: 'danger',
                            });
                        } else {
                            // All items failed
                            const errorMessage = validationIssues
                                .map(issue => `${issue.Ref}: ${issue.Issue}`)
                                .join('; ');
                            
                            setAddToCartTip({
                                message: `Failed to add items: ${errorMessage}`,
                                timestamp: Date.now(),
                                placement: 'top',
                                level: 'danger',
                            });
                        }
                    }
                } : null,
                cartApiOptions: { expand: ['forms', 'ordertotals'], update: ['lineitems', 'promotions'] },
                gtmPayload: gtmPayload
            });
            
        } catch (error) {
            console.error('Bulk add to cart failed:', error);
            setAddToCartTip({
                message: 'An error occurred while adding items to cart. Please try again.',
                timestamp: Date.now(),
                placement: 'top',
                level: 'danger',
            });
        } finally {
            setIsLoading(false);
        }
    }, [bulkAddItemsToCart, clearAllInputs, gtmListValue, productInfo, rows, statusTooltip, totalQty]);

    React.useEffect(() => {
        setIsLoading(false);
    }, [orderLastUpdate]);

    React.useEffect(() => {
        if (!isMobile) {
            // Create a style element with higher specificity
            const styleElement = document.createElement('style');

            styleElement.id = 'body-overflow-override';
            styleElement.textContent = `
                html body {
                    overflow: unset !important;
                    overflow-x: visible !important;
                    overflow-y: unset !important;
                }
            `;
            document.head.appendChild(styleElement);
            
            return () => {
                // Remove the injected style
                const existingStyle = document.getElementById('body-overflow-override');

                if (existingStyle) {
                    existingStyle.remove();
                }
            };
        }
    }, [isMobile]);

    // Desktop accordion data - updated to include facet columns
    const accordionData = React.useMemo(() => {
        return buyInBulkTableData.map((data) => {
            const baseRowValues = [
                {
                    value: (
                        <div className="d-flex justify-content-center align-items-center">
                            <img
                                className="thumbnail mr-3 mr-md-0"
                                src={getImageUrl(data.imageUrl, 49, 82)}
                                aria-hidden
                                alt=""
                            />
                            <ProductBadge badges={data.badges} className="mr-2" style={{ fontSize: '.8em' }} />
                        </div>
                    ),
                    id: data.imageUrl,
                },
                {
                    value: data.id,
                    id: data.id,
                }
            ];
            
            // Add facet columns
            const facetRowValues = productFacets.map((facet) => {
                const facetValue = getFacetValueForProduct(data, facet.Attribute);

                return {
                    value: facetValue || '-',
                    id: `${facet.Attribute}-${data.id}`,
                };
            });
            
            const endRowValues = [
                {
                    value: <Price priceInfo={data.priceInfo} quantity={rows[data.id]?.quantity} />,
                    id: 'Commerce.Product.Tabs.Price.Label',
                },
                {
                    value: (data.productInfo.StockStatus == "InStock" ? 
                    (
                        <div className='NumberInput'>
                            <div className='num-input'>
                                <NumberInput {...getNumberInputProps(data)} />
                            </div>
                        </div>
                        ) : (<ProductBadge badges={["Out Of Stock"]}/>)),
                    id: 'Commerce.Product.Tabs.BuyInBulk.Quantity.Label',
                }
            ];

            return {
                id: data.id,
                rowValues: [...baseRowValues, ...facetRowValues, ...endRowValues],
            };
        });
    }, [buyInBulkTableData, productFacets, rows, getNumberInputProps]);

    const isMobile = forceMobile || viewport.is.mobile;

    return (
        <div
            id={id}
            role="tabpanel"
            aria-labelledby={labelledBy}
            className={cx('ProductBuyInBulk tab-pane fade show', className)}
            style={{ position: 'relative', marginBottom: isMobile ? '15px' : '0'}}
        >
            {!isMobile && (
                <>
                    <BulkAddToCart
                        addToCartTip={addToCartTip}
                        cartIsUpdating={cartIsUpdating}
                        isLoading={isLoading} 
                        isMobile={isMobile}
                        onBulkAddToCart={onBulkAddToCart}
                        selectedItems={selectedItems}  
                    />
                </>
                
            )}
            
            <div className={isMobile ? 'mobile-card-container' : 'table-responsive'}>
                {isMobile ? (
                    <div>
                        <div>
                            <BulkAddToCart
                                addToCartTip={addToCartTip}
                                cartIsUpdating={cartIsUpdating}
                                isLoading={isLoading} 
                                isMobile={isMobile}
                                onBulkAddToCart={onBulkAddToCart}
                                selectedItems={selectedItems}  
                            />
                        </div>
                        
                        <div className="ProductBuyInBulk__product-carousel">
                            {buyInBulkTableData.map((item) => (
                                <div key={item.id} className='mobile-buy-in-bulk-card'>
                                    <BuyBulkProductCard 
                                        facets={productFacets}
                                        getFacetValueForProduct={getFacetValueForProduct}
                                        getImageUrl={getImageUrl}
                                        getNumberInputProps={getNumberInputProps}
                                        item={item}
                                        priceLabel={priceLabel}
                                        rows={rows}
                                        quantityLabel={quantityLabel}
                                        setAddToCartTip={setAddToCartTip}
                                        skuLabel={skuLabel}
                                    />
                                </div>
                            ))}
                            
                        </div>
                        <ScrollIndicator targetSelector='.ProductBuyInBulk__product-carousel' itemClass='.mobile-buy-in-bulk-card' />
                        <BuyBulkSummary 
                            addToCartTip={addToCartTip}
                            cartIsUpdating={cartIsUpdating}
                            clearAllInputs={clearAllInputs}
                            facets={productFacets}
                            getFacetValueForProduct={getFacetValueForProduct}
                            getImageUrl={getImageUrl}
                            isLoading={isLoading}
                            isMobile={isMobile}
                            onBulkAddToCart={onBulkAddToCart}
                            selectedItems={selectedItems}
                            updateRow={updateRow}
                        />
                    </div>
                ) : (
                    <div style={{ marginBottom: '60px'}}>
                        <BuyInBulkAccordion accordionData={accordionData} accordionHeaders={accordionHeaders} />
                    </div>
                )}
            </div>
            {isLoading ? <Loader  /> : null}
        </div>
    );
}

ProductBuyInBulk.propTypes = {
    bulkAddItemsToCart: px.func,
    buyInBulkTableData: px.arrayOf(
        px.shape({
            content: px.arrayOf(
                px.shape({ values: px.arrayOf(px.shape({ node: px.node, id: px.any })), label: px.string })
            ),
            id: px.string,
            imageUrl: px.string,
            priceInfo: px.object,
            hasStock: px.bool,
            gtaPayload: px.object,
            productInfo: Product,
            swatch: px.string,
            badges: px.array
        })
    ),
    className: px.string,
    gtmListValue: px.string,
    id: px.string,
    labelledBy: px.string,
    forceMobile: px.bool,
    orderLastUpdate: px.number,
    Price: px.elementType,
    productFacets: px.arrayOf(
            px.shape({
                Attribute: px.string,
                AttributeDefinition: px.string,
                AttributeDisplayName: px.string,
                AttributeFacetLabel: px.string,
                DependentAttribute: px.string,
                FacetColors: px.array,
                FacetRanges: px.array,
                FacetValues: px.array,
                SelectorControlType: px.string
            })
        ),
    productInfo: Product,
    isUserAuthenticated: px.bool,
    statusTooltip: px.oneOf([false, true, undefined, null, 'top', 'bottom', 'right']),
    totalQty: px.number,
};